'use client';
import { useRef, useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';

export type SpotlightProps = {
  className?: string;
  size?: number;
};

export function Spotlight({
  className,
  size = 400,
}: SpotlightProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [parentElement, setParentElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      const parent = containerRef.current.parentElement;
      if (parent) {
        parent.style.position = 'relative';
        parent.style.overflow = 'hidden';
        setParentElement(parent);
      }
    }
  }, []);

  const parentRectRef = useRef<{ left: number; top: number } | null>(null);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!parentElement || !containerRef.current) return;
      if (!parentRectRef.current) {
        parentRectRef.current = parentElement.getBoundingClientRect();
      }
      const { left, top } = parentRectRef.current;
      const x = event.clientX - left - size / 2;
      const y = event.clientY - top - size / 2;
      containerRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    },
    [size, parentElement]
  );

  useEffect(() => {
    if (!parentElement) return;
    const enter = () => {
      parentRectRef.current = parentElement.getBoundingClientRect();
      setIsHovered(true);
    };
    const leave = () => {
      parentRectRef.current = null;
      setIsHovered(false);
    };
    parentElement.addEventListener('mousemove', handleMouseMove);
    parentElement.addEventListener('mouseenter', enter);
    parentElement.addEventListener('mouseleave', leave);
    return () => {
      parentElement.removeEventListener('mousemove', handleMouseMove);
      parentElement.removeEventListener('mouseenter', enter);
      parentElement.removeEventListener('mouseleave', leave);
    };
  }, [parentElement, handleMouseMove]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'pointer-events-none absolute rounded-full blur-3xl opacity-0 transition-opacity duration-300 ease-out',
        isHovered && 'opacity-100',
        className
      )}
      style={{
        width: size,
        height: size,
        background: 'radial-gradient(circle at center, hsl(var(--primary) / 0.15), transparent 80%)',
        transition: 'transform 0.15s cubic-bezier(0.1, 0.8, 0.2, 1), opacity 0.3s ease',
        willChange: 'transform, opacity',
      }}
    />
  );
}

